/**
 * @file useDesktopPinHolders.ts
 * @description Everyone who keeps a given artifact on their computer, for the
 * claim banner. Consumer: the banner on the form and record pages.
 *
 * This is the only reader of `User.desktopPinPaths`, and the reason that
 * derived field exists: Firestore cannot query map values under arbitrary keys,
 * so `desktopPins` alone would make this question cost a read of every user
 * document. The query is a bare `array-contains` on a single field, which
 * Firestore indexes automatically — `firestore.indexes.json` needs no entry,
 * and `users` carries no `fieldOverrides` that would exempt it.
 *
 * `since` is `meta.created` and nothing else. No recency is available and none
 * is invented: the banner shows the date and lets the reader draw their own
 * conclusion from it. The signed-in user is included; a caller that wants
 * "other people" filters them out. A failed query reads as no holders, which
 * hides the banner rather than asserting anything false.
 *
 * `name` and `email` are checked at runtime although `User` declares them
 * required: nothing enforces that on write, and the sort's tiebreak — reached
 * whenever two holders both have an unreadable `created` — would throw on an
 * absent name. An avatar with no initials is a worse banner; a crash is no
 * banner and no page.
 */

import { useEffect, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useSnapshot } from 'valtio';

import type { TimeType, User } from '#types';
import { createCollectionStore, type CollectionStore } from '../../store';
import { resolveDesktopPinEntries } from './resolveDesktopPinEntries';

export type DesktopPinHolder = {
    /** Document id in `users`. */
    userId: string;
    name: string;
    email: string;
    /** `meta.created` of that user's pin. Undefined when the pin predates the field or is unresolved. */
    since: TimeType | undefined;
};

const NO_HOLDERS: readonly DesktopPinHolder[] = [];

const inactiveStore = createCollectionStore<User>('__inactive__');

const createdMillis = (since: TimeType | undefined): number =>
    since instanceof Timestamp ? since.toMillis() : Number.POSITIVE_INFINITY;

export const useDesktopPinHolders = (pinPath: string | null | undefined): {
    holders: readonly DesktopPinHolder[];
    loading: boolean;
} => {
    const store = useMemo<CollectionStore<User>>(
        () => pinPath
            ? createCollectionStore<User>('users', {
                queryOptions: {
                    extraConstraints: [
                        { type: 'where', field: 'desktopPinPaths', op: 'array-contains', value: pinPath },
                    ],
                },
            })
            : inactiveStore,
        [pinPath],
    );

    useEffect(() => {
        if (!pinPath) return;
        store.connect();
        return () => store.disconnect();
    }, [pinPath, store]);

    const snap = useSnapshot(store.state);

    const holders = useMemo<readonly DesktopPinHolder[]>(() => {
        if (!pinPath) return NO_HOLDERS;

        return snap.docIds
            .flatMap((userId) => {
                const holder = snap.docs.get(userId)?.data as User | undefined;
                const entry = resolveDesktopPinEntries(holder?.desktopPins, pinPath)[0];
                if (!holder || !entry) return [];
                return [{
                    userId,
                    name: typeof holder.name === 'string' ? holder.name : '',
                    email: typeof holder.email === 'string' ? holder.email : '',
                    since: entry.pin.meta?.created,
                }];
            })
            .sort((a, b) => createdMillis(a.since) - createdMillis(b.since) || a.name.localeCompare(b.name));
    }, [pinPath, snap.docIds, snap.docs]);

    return { holders, loading: pinPath ? snap.loading : false };
};
